/**
 * The command registry is the backbone of the UI: every action lives in one
 * place and drives the menu item, the toolbar button, the keyboard shortcut,
 * the command palette and the enabled/checked state. Adding a feature means
 * adding one object — that is what keeps the app coherent as it grows.
 */

export type MenuId = 'file' | 'edit' | 'view' | 'image' | 'adjust' | 'layer' | 'help';

export interface Command<Ctx> {
  /** Stable, namespaced id. Never rename: it is used by shortcuts and tests. */
  id: string;
  /** Menu label. */
  title: string;
  /** Which top-level menu hosts this command. Omit for palette/shortcut-only. */
  menu?: MenuId;
  /** Menu grouping; a separator is drawn between sections. */
  section?: number;
  /** Sort order inside a section (lower first). */
  order?: number;
  /**
   * `mod+shift+z` style spec, see core/input/shortcuts. May be a list when one
   * action has several natural bindings (e.g. zoom in on `+` and `=`).
   */
  shortcut?: string | string[];
  /** Render a checkmark and keep the menu open after running. */
  toggle?: boolean;
  /**
   * Roadmap marker: the command is listed but inert until that iteration ships.
   * Keeps the menu honest about what the build can actually do.
   */
  plannedFor?: number;
  /** Tooltip / status-line hint. */
  description?: string;
  isEnabled?(ctx: Ctx): boolean;
  isVisible?(ctx: Ctx): boolean;
  isChecked?(ctx: Ctx): boolean;
  run(ctx: Ctx): void | Promise<void>;
}

export type CommandResult = { ok: true } | { ok: false; reason: string };

export function isPlanned(command: Command<unknown>): boolean {
  return command.plannedFor !== undefined;
}

/** Normalised shortcut list for a command (possibly empty). */
export function shortcutsOf(command: Command<unknown>): string[] {
  if (!command.shortcut) return [];
  return Array.isArray(command.shortcut) ? command.shortcut : [command.shortcut];
}
